import React, { useEffect, useRef, useState } from 'react'
import { Divider, Menu, MenuItem } from '@mui/material'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import { NestedMenuItem } from 'mui-nested-menu'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import CheckMark from '@assets/images/icons/check.svg'
import IconArrowRight from '@assets/images/icons/arr_right.svg'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import DividerWithText from '@/modules/base/components/divider-with-text/divider-with-text'
import { uniqBy } from 'lodash'

const useStyles = makeStyles((theme) => ({
  cascadingContextMenu: props => ({
    height: props.height,
  }),
  contentWrapper: {
    height: '100%',
  },
  menu: {
    pointerEvents: ({ killed }) => killed ? 'none' : 'all',
  },
  menuPaper: {
    minWidth: 300,
    borderRadius: 8,
  },
  menuList: {
    padding: '8px',
  },
  nestedMenuList: {
    padding: 0,
  },
  menuItem: {
    minHeight: 44,
    padding: `${CssHelper.px(2, 10)} !important`,
    borderRadius: '6px',
    color: theme.textColor,
    transition: 'background-color 120ms ease, color 120ms ease',
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: theme.primaryBrightBackground,
    },
    '&.Mui-disabled': {
      opacity: '0.45',
    },
  },
  activePathItem: {
    backgroundColor: `${theme.primaryBrightBackground} !important`,
  },
  checkIconWrapper: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  checkIcon: {
    marginLeft: 10,
    fill: theme.green,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    lineHeight: 1,
    minHeight: 36,
    padding: CssHelper.px(8, 0),
  },
  labelText: {
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  icon: {
    width: 16,
    minWidth: 16,
    marginRight: 12,
    color: theme?.icons?.base || 'inherit',
    fill: 'currentColor !important',
    '& *': {
      fill: 'currentColor !important',
    },
  },
  iconPlaceholder: {
    width: 16,
    minWidth: 16,
    marginRight: 12,
  },
  nestedLabel: {
    justifyContent: 'flex-start',
  },
  nestedLeft: {
    display: 'flex',
    alignItems: 'center',
    lineHeight: 1,
    minWidth: 0,
    width: '100%',
  },
  nestedText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  nestedCaret: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 8,
    color: theme?.icons?.base || 'inherit',
    fill: 'currentColor !important',
    '& *': {
      fill: 'currentColor !important',
    },
  },
  divider: {
    margin: '8px 10px',
  },
  textDivider: {
    margin: CssHelper.px(6, 12, 2),
    '&:before': {
      maxWidth: 18,
    },
  },
  dividerLabel: {
    maxWidth: 196,
    overflow: 'hidden',
    textWrap: 'nowrap',
    textOverflow: 'ellipsis',
  },
  border: {
    paddingBottom: 0,
    '& .label': {
      padding: CssHelper.px(8, 0),
      borderBottom: theme.border,
    },
  },
  headerItem: {
    maxWidth: 270,
    minHeight: 44,
    borderRadius: 0,
    pointerEvents: 'none',
    justifyContent: 'center',
    padding: `${CssHelper.px(0, 0)} !important`,
    marginBottom: 4,
    '&, &:hover, &.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
    '&.Mui-disabled': {
      opacity: 1,
    },
    '& .headerTitle': {
      display: 'block',
      width: '100%',
      textAlign: 'center',
      fontWeight: 600,
      fontSize: 14,
      lineHeight: 1.33,
      letterSpacing: 0,
      color: theme.textColor,
      boxSizing: 'border-box',
      margin: '0 auto',
      padding: CssHelper.px(10, 16, 12),
      borderBottom: theme.border,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
  },
}))

export const CascadingContextMenu = (props) => {
  const {
    menuItems,
    children,
    disabled,
    hoverParentClass, // ag-grid hack
    position,
    vertical,
    horizontal,
    onClose,
    onHoverTarget,
    closeOnClick,
  } = props

  const classes = useStyles(props)
  const [menuPosition, setMenuPosition] = useState(null)
  const [activePath, setActivePath] = useState(null)
  const nestedMenuProps = {
    classes: { paper: classes.menuPaper },
    MenuListProps: { className: classes.nestedMenuList },
  }

  const renderIcon = (icon, className, height = '16px', width = '16px') => {
    if (!icon) return null

    // Check if icon is a React element (e.g., MUI Icon component instance)
    if (React.isValidElement(icon)) {
      return <span className={className}>{icon}</span>
    }

    // Otherwise, treat it as an SVG import
    return <SVGIcon className={className} svg={icon} height={height} width={width} />
  }

  useEffect(() => {
    setMenuPosition(position)
  }, [position])

  const handleRightClick = (event) => {
    event.preventDefault()
    if (menuPosition || disabled) {
      return
    }
    setMenuPosition({
      top: event.pageY,
      left: event.pageX,
    })
  }

  const handleItemClick = (callback, e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    closeOnClick && close()
    callback && callback(e)
  }

  const isPathActive = (itemPath) => {
    if (!activePath) {
      return false
    }
    return activePath === itemPath || activePath.startsWith(`${itemPath}/`)
  }

  const getItem = (item, index, parentPath = '') => {
    const { children, onClick, label, icon, disabled, checked, customClass, border, key, type } = item
    const itemPath = `${parentPath}${parentPath ? '/' : ''}${key ?? index}`
    const isActivePathItem = isPathActive(itemPath)
    const menuItemClassName = clsx(classes.menuItem, customClass, { [classes.border]: border, [classes.activePathItem]: isActivePathItem })

    if (type === 'header') {
      return (
        <MenuItem disabled key={key ?? `${label}_header`} className={clsx(classes.menuItem, classes.headerItem)}>
          <span className='headerTitle'>{label}</span>
        </MenuItem>
      )
    }

    if (!onClick && !children?.length) {
      if (label === '-') {
        return <Divider key={`divider${Math.random()}`} className={classes.divider} />
      } else {
        return <DividerWithText solid key={`divider${Math.random()}`} className={classes.textDivider} text={<span className={classes.dividerLabel}>{label}</span>} />
      }
    }

    if (children && !disabled) {
      const nestedContent = (
        <span className={clsx(classes.label, classes.nestedLabel, 'label')}>
          <span className={classes.nestedLeft}>
            {icon ? renderIcon(icon, classes.icon) : <span className={classes.iconPlaceholder} />}
            <span className={clsx(classes.nestedText, classes.labelText)}>{label}</span>
          </span>
        </span>
      )

      return (
        <NestedMenuItem
          className={menuItemClassName}
          key={key ?? itemPath}
          label={label}
          renderLabel={() => nestedContent}
          rightIcon={renderIcon(IconArrowRight, classes.nestedCaret, '14px', '14px')}
          parentMenuOpen={!!menuPosition}
          children={children.map((child, childIndex) => getItem(child, childIndex, itemPath))}
          MenuProps={nestedMenuProps}
          onClick={(e) => {
            if (!onClick) {
              return
            }
            handleItemClick(onClick, e)
          }}
          onMouseEnter={(e) => {
            setActivePath(itemPath)
            onHoverTarget(label)
          }}
          onMouseLeave={() => onHoverTarget(null)}
        />
      )
    }

    const checkedIcon = checked
      ? <span className={classes.checkIconWrapper}><SVGIcon className={classes.checkIcon} svg={CheckMark} height='16px' width='16px'></SVGIcon></span>
      : null

    const content = (
      <span className={clsx(classes.label, 'label')}>
        {icon ? renderIcon(icon, classes.icon) : <span className={classes.iconPlaceholder} />}
        <span className={classes.labelText}>{label}</span>
        {checkedIcon}
      </span>
    )

    return (
      <MenuItem disabled={disabled}
        key={key ?? itemPath}
        onClick={(e) => handleItemClick(onClick, e)}
        className={menuItemClassName}
        onMouseEnter={(e) => {
          setActivePath(itemPath)
          onHoverTarget(label)
        }}
        onMouseLeave={() => onHoverTarget(null)}>
        {content}
      </MenuItem>
    )
  }

  const items = menuPosition ? menuItems.map((item, i) => getItem(item, i)) : []

  const getParent = (child, className) => {
    const parent = child.offsetParent
    if (!parent) return null
    if (Array.from(parent.classList).includes(className)) {
      return parent
    }

    return getParent(parent, className)
  }

  useEffect(() => {
    if (wrapperRef.current) {
      if (hoverParentClass) { // ag-grid hack
        const parent = getParent(wrapperRef.current, hoverParentClass)
        if (menuPosition) {
          parent && parent.classList.add('hover')
        } else {
          parent && parent.classList.remove('hover')
        }
      } else {
        if (menuPosition) {
          Array.from(wrapperRef.current.children).forEach(child => {
            child.classList.add('hover')
          })
        } else {
          Array.from(wrapperRef.current.children).forEach(child => {
            child.classList.remove('hover')
          })
        }
      }
    }
  }, [menuPosition])
  const wrapperRef = useRef()

  const close = () => {
    setMenuPosition(null)
    setActivePath(null)
    onClose()
  }

  return (
    <div className={classes.cascadingContextMenu} onContextMenu={handleRightClick}>
      <div className={classes.contentWrapper} ref={wrapperRef}>{children}</div>
      <Menu
        open={!!menuPosition}
        onClose={close}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition}
        transformOrigin={{
          vertical,
          horizontal,
        }}
        className={classes.menu}
        PaperProps={{ className: classes.menuPaper }}
        MenuListProps={{ className: classes.menuList }}
      >
        {uniqBy(items, 'key')}
      </Menu>
    </div>
  )
}

CascadingContextMenu.propTypes = {
  disabled: PropTypes.bool,
  children: PropTypes.any.isRequired,
  menuItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  height: PropTypes.string,
  vertical: PropTypes.string,
  horizontal: PropTypes.string,
  onClose: PropTypes.func,
  onHoverTarget: PropTypes.func,
  killed: PropTypes.bool,
  closeOnClick: PropTypes.bool,
  position: PropTypes.any,
}

CascadingContextMenu.defaultProps = {
  horizontal: 'right',
  vertical: 'top',
  onClose: () => {},
  onHoverTarget: () => {},
  closeOnClick: true,
}

export default CascadingContextMenu
